import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { useWarningSummary, severityStyle, typeLabel } from "../lib/warnings";
import { useT } from "@/i18n/I18nProvider";

/**
 * Sticky, persistent warning banner shown above the business dashboard
 * content. Always reflects the highest-severity active warning.
 *
 * Users cannot permanently dismiss the bar: it only disappears when the
 * underlying warning is closed (with a reason) inside the Warning Center.
 * (This is intentional per the PRD.)
 *
 * `data-testid="ops-warning-banner"` is the anchor for tests.
 */
export default function WarningBanner() {
  const { t } = useT();
  const { summary, loading } = useWarningSummary();
  const [collapsed, setCollapsed] = useState(false);

  if (loading || !summary?.top) return null;
  const top = summary.top;
  const style = severityStyle(top.severity);

  const totalLabel = summary.count === 1
    ? t("warnings.banner.count_one")
    : t("warnings.banner.count_many").replace("{count}", summary.count);

  if (collapsed) {
    return (
      <button
        data-testid="ops-warning-banner-collapsed"
        onClick={() => setCollapsed(false)}
        className={`mb-4 w-full text-left rounded-lg border ${style.banner} px-4 py-2 flex items-center justify-between hover:brightness-95 transition`}
      >
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          <span className={`w-2 h-2 rounded-full ${style.dot}`} />
          <span className={style.title}>{totalLabel}</span>
        </span>
        <span className="text-xs text-slate-600 underline">{t("warnings.banner.expand")}</span>
      </button>
    );
  }

  return (
    <div
      data-testid="ops-warning-banner"
      className={`mb-6 rounded-xl border ${style.banner} overflow-hidden shadow-sm`}
    >
      <div className={`h-1 ${style.bar}`} />
      <div className="p-4 sm:p-5 flex flex-col sm:flex-row gap-3 sm:gap-4 sm:items-start">
        <div className={`w-9 h-9 rounded-lg grid place-items-center bg-white border ${style.chip} flex-shrink-0`}>
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${style.title}`}>
              {style.label}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${style.chip}`}>
              {typeLabel(top.warning_type, t)}
            </span>
            <span className="text-xs text-slate-500">· {totalLabel}</span>
          </div>
          <div className={`font-semibold mt-1 ${style.title}`} data-testid="ops-warning-top-title">
            {top.title}
          </div>
          <div className={`text-sm mt-0.5 ${style.body}`}>
            {top.message}
          </div>
          {top.recommended_action && (
            <div className="text-xs mt-2 text-slate-600">
              <span className="font-semibold">{t("warnings.recommended")}:</span> {top.recommended_action}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 self-start">
          <Link
            to="/app/warnings"
            data-testid="ops-warning-open-center"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium text-slate-800"
          >
            {t("warnings.page.title")} <ChevronRight className="w-3.5 h-3.5" />
          </Link>
          <button
            data-testid="ops-warning-collapse"
            onClick={() => setCollapsed(true)}
            className="p-1.5 rounded-md hover:bg-white text-slate-600"
            title={t("warnings.banner.minimize")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
